import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';

type CodeInputProps = {
  length?: number;
  onComplete: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
  onChange?: (code: string) => void;
};

export const CodeInput = ({
  length = 6,
  onComplete,
  disabled = false,
  error = false,
  onChange,
}: CodeInputProps) => {
  const [code, setCode] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (code.length === length && !disabled && !hasSubmitted) {
      setHasSubmitted(true);
      onComplete(code);
    }
  }, [code, length, onComplete, disabled, hasSubmitted]);

  const handleChange = (value: string) => {
    setCode(value);

    if (hasSubmitted) {
      setHasSubmitted(false);
    }

    if (onChange) {
      onChange(value);
    }
  };

  const slotClassName = cn(
    'text-foreground',
    error
      ? 'border-destructive'
      : 'focus-within:ring-primary',
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-center">
        <InputOTP
          maxLength={length}
          value={code}
          onChange={handleChange}
          disabled={disabled}
          autoComplete="one-time-code"
          pattern="^\d*$"
          aria-label="Verification code"
        >
          <InputOTPGroup className="gap-2">
            {Array.from({ length }).map((_, index) => (
              <InputOTPSlot
                key={index}
                index={index}
                className={cn(
                  slotClassName,
                  'rounded-md border',
                )}
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      {error && (
        <p className="text-destructive text-xs sm:text-sm text-center">
          Invalid verification code. Please try again.
        </p>
      )}
    </div>
  );
};
